import {
  dashboardExact as exact,
  dashboardRecord as record,
  dashboardRegisterId as registerId,
  dashboardString as string,
  dashboardStringArray as stringArray,
  dashboardValidationFail as fail,
  type DashboardUnknownRecord as UnknownRecord,
} from './FmDashboardOverview.validationHelpers';

const RECORD_CONTEXT_KEYS = [
  'scope', 'rankingRunId', 'recordId', 'entityId', 'propertyId', 'jobId',
  'sourceId', 'revision', 'campaignId', 'claimId', 'changeSummarySnapshot',
  'changeIds', 'citationIds',
] as const;

export function validateDashboardAssistantAction(
  value: unknown,
  path: string,
  ids: Set<string>,
  handlers: UnknownRecord,
): UnknownRecord {
  const action = record(value, path);
  exact(action, ['id', 'label', 'tool', 'context'], path);
  registerId(action.id, `${path}.id`, ids);
  string(action.label, `${path}.label`);
  const tool = string(action.tool, `${path}.tool`);
  if (!['explain', 'navigate'].includes(tool)) fail(`${path}.tool`, 'must be explain or navigate');
  if (typeof handlers[tool] !== 'function') fail(`${path}.tool`, `requires handlers.assistantTools.${tool}`);
  const context = record(action.context, `${path}.context`);
  if (tool === 'explain' && context.scope === 'portfolio') {
    exact(context, ['scope', 'rankingRunId', 'topRecordId'], `${path}.context`);
    string(context.rankingRunId, `${path}.context.rankingRunId`);
    if (context.topRecordId !== null) string(context.topRecordId, `${path}.context.topRecordId`);
    return action;
  }
  exact(context, tool === 'navigate' ? [...RECORD_CONTEXT_KEYS, 'destination'] : RECORD_CONTEXT_KEYS, `${path}.context`);
  if (context.scope !== 'record') fail(`${path}.context.scope`, 'must be record');
  ['rankingRunId', 'recordId', 'entityId', 'propertyId', 'jobId', 'sourceId', 'revision']
    .forEach((key) => string(context[key], `${path}.context.${key}`));
  ['campaignId', 'claimId', 'changeSummarySnapshot'].forEach((key) => {
    if (context[key] !== undefined) string(context[key], `${path}.context.${key}`);
  });
  ['changeIds', 'citationIds'].forEach((key) => {
    if (context[key] !== undefined) stringArray(context[key], `${path}.context.${key}`);
  });
  if (tool === 'navigate' && !['owner-page', 'upload', 'manual', 'valuation', 'watch'].includes(String(context.destination))) {
    fail(`${path}.context.destination`, 'contains an unsupported destination');
  }
  return action;
}

interface ExpectedRecordContext {
  rankingRunId: string;
  recordId: string;
  entityId: string;
  propertyId: string;
  jobId: string;
  sourceId: string;
  revision: string;
  claimId: string;
  changeSummary?: string;
  changeIds?: readonly string[];
  citationIds: readonly string[];
}

export function validateDashboardRecordAssistantContext(
  action: UnknownRecord,
  path: string,
  expected: ExpectedRecordContext,
): void {
  const context = record(action.context, `${path}.context`);
  if (context.scope !== 'record') fail(`${path}.context.scope`, 'must target this record');
  Object.entries(expected).forEach(([key, value]) => {
    if (!['claimId', 'changeSummary', 'changeIds', 'citationIds'].includes(key) && context[key] !== value) {
      fail(`${path}.context.${key}`, `must match record ${key}`);
    }
  });
  const evidenceKeys = ['claimId', 'changeSummarySnapshot', 'changeIds', 'citationIds'] as const;
  const provided = evidenceKeys.filter((key) => context[key] !== undefined).length;
  if (expected.changeSummary !== undefined && provided !== evidenceKeys.length) {
    fail(`${path}.context`, 'must provide claimId, changeSummarySnapshot, changeIds, and citationIds for evidence records');
  }
  if (expected.changeSummary === undefined && provided !== 0 && provided !== evidenceKeys.length) {
    fail(`${path}.context`, 'must provide the complete evidence context or omit it');
  }
  if (provided === 0) return;
  if (context.claimId !== expected.claimId) fail(`${path}.context.claimId`, 'must match the visible evidence claim');
  if (context.changeSummarySnapshot !== expected.changeSummary) {
    fail(`${path}.context.changeSummarySnapshot`, 'must match the visible served change');
  }
  const changeIds = stringArray(context.changeIds, `${path}.context.changeIds`);
  if (!expected.changeIds || changeIds.length !== expected.changeIds.length
    || changeIds.some((id, index) => id !== expected.changeIds?.[index])) {
    fail(`${path}.context.changeIds`, 'must match the visible served changes in order');
  }
  const citationIds = stringArray(context.citationIds, `${path}.context.citationIds`);
  if (citationIds.length !== expected.citationIds.length
    || citationIds.some((id, index) => id !== expected.citationIds[index])) {
    fail(`${path}.context.citationIds`, 'must match the visible evidence citations in order');
  }
}
